import type { IAccount } from "@/modules/account/common/types";
import { TransferKeys } from "@/modules/transaction/common/enum";
import { getAccountsFilteredList } from "@/modules/transaction/helpers";
import { useCreateBeneficiaryMutation } from "@/modules/transaction/services";
import { AllowedAccountTransferCurrencies } from "@/shared/common/constants";
import { BeneficiaryType } from "@/shared/common/enums";
import type { IObject, TSetError } from "@/shared/common/types";
import { isAmeriaAccount, isValidAccount, isValidAccountChecksum } from "@/shared/helpers";
import { useGetAccountHolder, useIsMounted, useNotify } from "@/shared/hooks";
import { debounce } from "@/shared/utils";
import type { SingleValue, TOption } from "@ab.uitools/ui-library/components/ReactSelect/types";
import { useFormProps } from "@ab.uitools/ui-library/hooks/useFormProps";
import { type Dispatch, type SetStateAction, useMemo, useRef, useState } from "react";

interface IUseAccountTransferParams {
  accountsList: IAccount[];
  commissionFieldKeys: string[];
  beneficiaryName: string;
  setIsAccountNumberInvalid: Dispatch<SetStateAction<boolean>>;
}

interface IHandleChangeBeneficiaryAccountOptions {
  beneficiary?: string;
  onAmeriaAccount?: () => void;
}

export const useAccountTransfer = ({
  accountsList,
  beneficiaryName,
  commissionFieldKeys,
  setIsAccountNumberInvalid,
}: IUseAccountTransferParams) => {
  const { showErrorAlertNotification } = useNotify();
  const isMounted = useIsMounted();

  const {
    setValue,
    setError,
    clearErrors,
    watch,
    errors = {},
    reset
  } = useFormProps();
  const {
    accountData,
    getAccountInfo,
    clearAccountData,
  } = useGetAccountHolder({ setIsAccountNumberInvalid });

  const [isBenSaved, setIsBenSaved] = useState<boolean>(false);
  const [createNewBeneficiary] = useCreateBeneficiaryMutation();

  const values = watch?.() || {};

  const beneficiaryNameRef = useRef(beneficiaryName);
  beneficiaryNameRef.current = beneficiaryName;

  const handleChangeBeneficiaryAccount = useMemo(
    () => debounce((account: string, options?: IHandleChangeBeneficiaryAccountOptions) => {
      if (!isValidAccount(account)) {
        setValue(TransferKeys.Beneficiary, "");
        clearAccountData();
        return;
      }
      setValue(TransferKeys.CreditAccount, account || "", { shouldValidate: true });
      if (isAmeriaAccount(account)) {
        options?.onAmeriaAccount?.();
      }
      if (!options?.beneficiary) {
        if (isMounted.current) {
          setValue(TransferKeys.BeneficiaryLegalStatus, null);
        }
        setValue(TransferKeys.Beneficiary, "");
      }

      getAccountInfo({
        account,
        clearErrors,
        setValue,
        getBeneficiaryName: () => options?.beneficiary || beneficiaryNameRef.current,
        setError: setError as TSetError,
        beneficiaryNameKey: TransferKeys.Beneficiary,
        creditAccountKey: TransferKeys.CreditAccount,
      });
    }, 700),
    [],
  );

  const handleSaveBeneficiary = () => {
    createNewBeneficiary({
      number: values.creditAccount,
      name: values.beneficiary,
      beneficiaryFund: BeneficiaryType.Account,
    })
      .unwrap()
      .then(() => {
        setIsBenSaved(true);
      })
      .catch((err) => {
        showErrorAlertNotification(err);
        setIsBenSaved(false);
      });
  };

  const handleClickTarget = (target: SingleValue<TOption>) => {
    const { currency } = target?.meta as IAccount;
    setIsBenSaved(false);
    setValue(TransferKeys.Currency, currency);
  };

  const accountsFilteredList = useMemo(() => {
    return getAccountsFilteredList({
      accountsList,
      accountFilters: AllowedAccountTransferCurrencies
    });
  }, [accountsList]);

  const saveBeneficiaryDeps = watch?.(
    [TransferKeys.Beneficiary, TransferKeys.CreditAccount],
  );

  const isSaveBeneficiaryEnabled = useMemo(() => {
    return !!(values.creditAccount
      && !errors.beneficiary
      && !errors.creditAccount
      && values.beneficiary);
  }, [saveBeneficiaryDeps]);

  const changes = watch?.(commissionFieldKeys).toString();

  const fieldChanges = useMemo(() => {
    const fieldValues: IObject = {};
    commissionFieldKeys.forEach(
      (fieldKey: string) => fieldValues[fieldKey] = watch?.(fieldKey)
    );
    return fieldValues;
  }, [changes]);

  const canGetCommission = useMemo(() => {
    const creditAccountStr = fieldChanges.creditAccount ? String(fieldChanges.creditAccount) : "";
    return !!(
      fieldChanges.debitAccount &&
      fieldChanges.creditAccount &&
      isValidAccountChecksum(creditAccountStr) &&
      isValidAccount(creditAccountStr) &&
      fieldChanges.amount
    );
  }, [fieldChanges]);

  return {
    watch,
    reset,
    values,
    errors,
    setValue,
    setError,
    isBenSaved,
    clearErrors,
    accountData,
    fieldChanges,
    canGetCommission,
    handleClickTarget,
    accountsFilteredList,
    handleSaveBeneficiary,
    isSaveBeneficiaryEnabled,
    handleChangeBeneficiaryAccount,
  };
};
